import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Edit2, Trash2, GraduationCap, ExternalLink, Video, BookOpen, Link2 } from "lucide-react";
import { CONTENT } from "@/constants/testIds";
import { useDepartments } from "@/hooks/useDepartments";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });

const TypeIcon = ({ type }) => {
  if (type === "video") return <Video className="w-4 h-4 text-red-500" />;
  if (type === "ebook") return <BookOpen className="w-4 h-4 text-blue-500" />;
  return <Link2 className="w-4 h-4 text-green-500" />;
};

const typeLabel = { video: "Vídeo", ebook: "E-book", link: "Link" };
const typeBg = { video: "bg-red-50 text-red-600", ebook: "bg-blue-50 text-blue-600", link: "bg-green-50 text-green-600" };

export default function TrainingsPage() {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(["autor_area", "administrador_total", "admin"]);
  const { departments } = useDepartments();
  const DEPTS = departments;
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", category: "", type: "video", media_url: "", department: "", tags: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => ax().get(`${API}/trainings`).then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = items.filter(i => {
    const matchSearch = i.title?.toLowerCase().includes(search.toLowerCase()) || i.category?.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "all" || i.type === typeFilter;
    return matchSearch && matchType;
  });

  const openCreate = () => { setEditing(null); setForm({ title: "", content: "", category: "", type: "video", media_url: "", department: "", tags: "" }); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, content: item.content, category: item.category || "", type: item.type || "video", media_url: item.media_url || "", department: item.department || "", tags: (item.tags || []).join(", ") }); setShowForm(true); };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, tags: form.tags ? form.tags.split(",").map(t => t.trim()) : [] };
    try {
      if (editing) await ax().put(`${API}/trainings/${editing.item_id}`, payload);
      else await ax().post(`${API}/trainings`, payload);
      setShowForm(false);
      load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este treinamento?")) return;
    await ax().delete(`${API}/trainings/${id}`);
    load();
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-[#2346ff]" />
          <div>
            <h1 className="text-xl font-bold font-heading text-slate-900">Treinamentos</h1>
            <p className="text-sm text-slate-500">{filtered.length} disponíveis</p>
          </div>
        </div>
        {canEdit && (
          <Button data-testid={CONTENT.createBtn} onClick={openCreate} style={{ backgroundColor: "#2346ff" }} className="text-white gap-2">
            <Plus className="w-4 h-4" /> Novo Treinamento
          </Button>
        )}
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid={CONTENT.searchInput} placeholder="Buscar treinamentos..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
      </div>

      <Tabs value={typeFilter} onValueChange={setTypeFilter} className="mb-4">
        <TabsList>
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="video">Vídeos</TabsTrigger>
          <TabsTrigger value="ebook">E-books</TabsTrigger>
          <TabsTrigger value="link">Links</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-48 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div data-testid={CONTENT.contentList} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400">
              <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum treinamento encontrado</p>
            </div>
          )}
          {filtered.map(item => (
            <div key={item.item_id} data-testid={CONTENT.contentItem} className="bento-card p-4 flex flex-col group">
              <div className="flex items-start justify-between mb-2">
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium ${typeBg[item.type] || typeBg.link}`}>
                  <TypeIcon type={item.type} />
                  {typeLabel[item.type] || item.type}
                </div>
                {canEdit && (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEdit(item)}><Edit2 className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => remove(item.item_id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                )}
              </div>
              <h3 className="font-semibold text-slate-800 text-sm mb-1 line-clamp-2 group-hover:text-[#2346ff] transition-colors">{item.title}</h3>
              <p className="text-xs text-slate-500 line-clamp-2 flex-1">{item.content}</p>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {item.category && <Badge variant="secondary" className="text-xs">{item.category}</Badge>}
                {item.department && <Badge className="text-xs" style={{ backgroundColor: "#e8ecff", color: "#2346ff" }}>{item.department}</Badge>}
              </div>
              {item.media_url && (
                <a href={item.media_url} target="_blank" rel="noreferrer"
                  className="mt-3 flex items-center gap-1 text-xs font-medium text-[#2346ff] hover:underline">
                  <ExternalLink className="w-3 h-3" /> Acessar conteúdo
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar Treinamento" : "Novo Treinamento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} /></div>
            <div><Label>Descrição *</Label><Textarea rows={3} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo *</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({...f, type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Vídeo</SelectItem>
                    <SelectItem value="ebook">E-book</SelectItem>
                    <SelectItem value="link">Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoria</Label>
                <Input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} placeholder="Ex: Vendas" />
              </div>
            </div>
            <div><Label>URL do Conteúdo</Label><Input value={form.media_url} onChange={e => setForm(f => ({...f, media_url: e.target.value}))} placeholder="https://..." /></div>
            <div>
              <Label>Departamento</Label>
              <Select value={form.department || "__none"} onValueChange={v => setForm(f => ({...f, department: v === "__none" ? "" : v}))}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Todos os departamentos</SelectItem>
                  {DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.title || !form.content} style={{ backgroundColor: "#2346ff" }} className="text-white">{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
